<template>
    <div id="audit-logs" class="setting-page-content-wrapper audit-logs">
        <div class="page-header">
            <div class="title">{{ $t("auditLogs.auditLogsTitle") }}</div>
            <div class="subtitle">
                {{ $t("auditLogs.auditLogsSub") }}
            </div>
        </div>

        <!-- Filter Panel -->
        <div class="audit-logs-filter-panel">
            <a-card :bordered="false" class="audit-logs-filter-card">
                <div class="audit-logs-filter-content">
                    <div class="audit-logs-filter-row">
                        <div class="audit-logs-filter-item">
                            <label class="audit-logs-filter-label">
                                <a-icon type="user" />
                                {{ $t("auditLogs.actor") }}
                            </label>
                            <a-select
                                :value="filter.actorIds"
                                mode="multiple"
                                :placeholder="$t('auditLogs.actorSelect')"
                                style="width: 100%;"
                                :maxTagCount="2"
                                allowClear
                                class="audit-logs-actor-select"
                                @change="(value) => { filter.actorIds = value; onFilterChange(); }"
                            >
                                <a-select-option value="system">{{ $t("auditLogs.actorSystem") }}</a-select-option>
                                <a-select-option value="chatbot">{{ $t("auditLogs.actorChatbot") }}</a-select-option>
                                <a-select-option value="api">{{ $t("auditLogs.actorApi") }}</a-select-option>
                                <a-select-option value="visitor">{{ $t("auditLogs.actorVisitor") }}</a-select-option>
                                <a-select-option value="agent">{{ $t("auditLogs.actorAgent") }}</a-select-option>
                                <a-select-option v-for="user in users" :key="user._id" :value="user._id">
                                    {{ user.name }}
                                </a-select-option>
                                <a-select-option v-for="user in (isAdmin ? adminUsers : [])" :key="user._id" :value="user._id">
                                    {{ user.name }} {{ $t("auditLogs.adminUserSuffix") }}
                                </a-select-option>
                            </a-select>
                        </div>
                        <div class="audit-logs-filter-item">
                            <label class="audit-logs-filter-label">
                                <a-icon type="filter" />
                                {{ $t("auditLogs.activityTypeLabel") }}
                            </label>
                            <a-select
                                :value="filter.activityTypes"
                                mode="multiple"
                                :placeholder="$t('auditLogs.activityTypeSelect')"
                                style="width: 100%;"
                                :maxTagCount="3"
                                allowClear
                                showSearch
                                class="audit-logs-activity-type-select"
                                :filterOption="filterActivityTypes"
                                @change="(value) => { filter.activityTypes = value; onFilterChange(); }"
                            >
                                <a-select-option v-for="type in activityTypes" :key="type.value" :value="type.value">
                                    {{ type.label }}
                                </a-select-option>
                            </a-select>
                        </div>
                        <div class="audit-logs-filter-item">
                            <label class="audit-logs-filter-label">
                                <a-icon type="calendar" />
                                {{ $t("auditLogs.dateRange") }}
                            </label>
                            <a-range-picker
                                :value="dateRange"
                                :placeholder="[$t('auditLogs.startDate'), $t('auditLogs.endDate')]"
                                format="DD.MM.YYYY"
                                style="width: 100%;"
                                @change="onDateRangeChange"
                                :allowClear="true"
                            />
                        </div>
                    </div>
                    <div class="audit-logs-filter-actions">
                        <a-button @click="resetFilters" :disabled="loading" class="audit-logs-reset-btn">
                            <a-icon type="reload" />
                            {{ $t("auditLogs.reset") }}
                        </a-button>
                        <a-button type="primary" @click="applyFilters" :loading="loading" class="audit-logs-apply-btn">
                            <a-icon type="search" />
                            {{ $t("auditLogs.filter") }}
                        </a-button>
                    </div>
                </div>
            </a-card>

            <!-- Active Filters -->
            <div class="audit-logs-active-filters" v-if="hasActiveFilters">
                <div class="audit-logs-active-filters-label">
                    <a-icon type="filter" />
                    {{ $t("auditLogs.activeFilters") }}
                </div>
                <div class="audit-logs-filter-tags">
                    <a-tag
                        v-for="actorId in filter.actorIds"
                        :key="actorId"
                        closable
                        @close="removeActorFilter(actorId)"
                        color="green"
                        class="audit-logs-filter-tag"
                    >
                        {{ getActorLabel(actorId) }}
                    </a-tag>
                    <a-tag
                        v-for="activityType in filter.activityTypes"
                        :key="activityType"
                        closable
                        @close="removeActivityTypeFilter(activityType)"
                        color="orange"
                        class="audit-logs-filter-tag"
                    >
                        {{ activityTypeLabels[activityType] || activityType }}
                    </a-tag>
                    <a-tag
                        v-if="filter.startDate"
                        closable
                        @close="removeDateFilter"
                        color="blue"
                        class="audit-logs-filter-tag"
                    >
                        <a-icon type="calendar" />
                        {{ formatDateRange() }}
                    </a-tag>
                </div>
            </div>
        </div>

        <!-- Table Section -->
        <section class="table-section">
            <a-card :bordered="false" class="table-card">
                <div class="table-header">
                    <div class="table-title">
                        <a-icon type="file-text" />
                        <span>{{ $t("auditLogs.auditLogsTable") }}</span>
                        <a-badge :count="activities.length" :number-style="{ backgroundColor: '#1890ff' }" />
                    </div>
                </div>
                <div v-if="activities.length === 0 && !loading" class="empty-state">
                    <a-empty :description="$t('auditLogs.noActivityYet')" />
                </div>
            <ve-table
                    v-else
                :columns="getColumns()"
                :table-data="activities"
                :row-style-option="rowStyleOption"
                :border-x="true"
                :border-y="true"
                :expand-option="expandOption"
                row-key-field-name="_id"
                id="audit-logs-table"
                :fixed-header="true"
                    class="audit-table"
            />
            </a-card>
            <div class="actions">
                <a-button
                    type="primary"
                    @click="loadNextPage"
                    :disabled="loading || !hasMore"
                    :loading="loading"
                    size="large"
                    v-if="activities.length"
                >
                    <a-icon type="down" />
                    {{ $t("settings.loadMore") }}
                </a-button>
            </div>
        </section>
    </div>
</template>
<script>
import Vue from "vue";
import { ACTIVITY_API } from "../../../../../../api/activity";
import moment from "moment";
import { PrismEditor } from "vue-prism-editor";
import "vue-prism-editor/dist/prismeditor.min.css"; // import the styles somewhere
// import highlighting library (you can use any library you want just return html string)
import { highlight, languages } from "prismjs/components/prism-core";
import "prismjs/components/prism-json";
import "prismjs/themes/prism-tomorrow.css";

// import language config
import SupsisButton from "../../../components/SupsisButton.vue";
import { mapActions, mapState } from "vuex";
import { VeLocale, VePagination } from "vue-easytable";

Vue.use(VePagination);
export default Vue.extend({
    name: "AuditLogs",
    components: { SupsisButton, PrismEditor },
    data(x) {
        return {
            tableConfig: null,
            loadingInstance: null,
            rowStyleOption: {
                stripe: true,
            },

            filter: {
                actorIds: [],
                startDate: null,
                endDate: null,
                activityTypes: [],
            },
            dateRange: [],
            activityTypeSearchQuery: "",
            activityTypeCustomSelected: [],
            loading: false,
            activities: [],
            totalCount: 0,
            hasMore: true,
            cursor: {
                page: 1,
                perPage: 50,
            },
            expandOption: {
                render: ({ row, column, rowIndex }, h) => {
                    return h(PrismEditor, {
                        props: {
                            value: JSON.stringify(row.payload || {}, null, 2),
                            class: "my-editor",
                            lineNumbers: true,
                            readOnly: true,
                            highlight: (code) => highlight(code, languages.json),
                        },
                    });
                },
            },
            activityTypeValues: [
                "blocked",
                "unblocked",
                "chat-opened",
                "chat-closed",
                "chat-rating",
                "chat-assigned-to-user",
                "chat-forwarded-to-user",
                "contact-owner-updated",
                "user-joined-to-chat",
                "ticket-assigned-to-user",
                "online",
                "offline",
                "page-changed",
                "create-ticket",
                "video-calling-started",
                "video-calling-closed",
                "voice-calling-started",
                "voice-calling-closed",
                "visitor-tag-added",
                "visitor-tag-removed",
                "note-created",
                "note-updated",
                "visitor-profile-updated",
                "user-added",
                "user-removed",
                "user-status-changed",
                "user-logged-in",
                "user-added-to-department",
                "user-removed-from-depart",
                "user-role-changed",
                "user-password-changed",
                "user-email-changed",
                "user-name-changed",
                "user-phone-changed",
                "user-disabled",
                "user-enabled",
                "settings-updated",
                "whatsapp-broadcast-created",
                "whatsapp-broadcast-completed",
                "whatsapp-broadcast-canceled",
                "whatsapp-broadcast-failed",
                "whatsapp-v1-bot-state-changed",
                "whatsapp-v2-bot-state-changed",
                "channel-created",
                "channel-removed",
                "channel-visibility-changed",
                "channel-zoho-config-updated",
                "channel-forward-config-updated",
                "login-form-activated",
                "login-form-deactivated",
                "login-form-updated",
                "chatbot-created",
                "chatbot-deleted",
                "chatbot-restored",
                "chatbot-published",
                "chatbot-webhook-added",
                "chatbot-webhook-removed",
                "banned-word-added",
                "banned-word-removed",
                "mail-address-added",
                "mail-address-removed",
                "mail-was-sent",
                "content-added",
                "content-removed",
                "role-added",
                "role-removed",
                "role-updated",
                "department-added",
                "department-removed",
                "tags-added",
                "tags-removed",
                "trigger-added",
                "trigger-removed",
                "trigger-updated",
                "welcome-message-added",
                "welcome-message-removed",
                "customer-group-added",
                "customer-group-removed",
                "customer-group-updated",
                "webhook-added",
                "webhook-removed",
                "webhook-updated",
                "contact-prop-added",
                "contact-prop-removed",
                "invoice-created",
                "invoice-updated",
                "invoice-payment-received",
                "invoice-payment-failed",
                "invoice-refunded",
                "invoice-deleted",
                "payment-method-added",
                "billing-address-updated",
                "plan-started",
                "plan-canceled",
                "task-workflow-created",
                "task-workflow-deleted",
                "task-workflow-restored",
                "task-workflow-permanently-deleted",
                "task-workflow-updated",
                "task-workflow-pipeline-stage-added",
                "task-workflow-pipeline-stage-updated",
                "task-workflow-pipeline-stage-deleted",
                "task-created",
                "task-deleted",
                "task-stage-changed",
                "task-workflow-changed",
                "task-assigned-to-user",
                "task-update-priority",
                "task-added-tags",
                "task-removed-tags",
                "task-update-title",
                "task-update-description",
                "task-update-start-date",
                "task-update-end-date",
                "task-update-owner",
                "task-added-followers",
                "task-removed-followers",
                "task-added-collaborators",
                "task-update-checklist",
                "task-added-subtask",
                "task-removed-subtask",
                "task-added-attachment",
                "task-comment-added",
                "task-comment-updated",
                "task-comment-deleted",
                "task-completed",
                "task-archived",
                "task-restored",
                "task-unarchived",
                "chatbot-credit-low",
                "chatbot-credit-done",
                "system-status",
                "whatsapp-credit-low",
                "reservation-created-plan",
                "reservation-updated-plan",
                "reservation-deleted-plan",
                "reservation-created",
                "reservation-updated",
                "reservation-deleted",
                "white-list-added",
                "white-list-removed",
                "site-suspended",
                "site-dead",
                "site-enabled",
                "custom",
                "auto-rebalance-success",
                "auto-rebalance-failed",
                "auto-rebalance-disabled",
                "automation-created",
                "automation-deleted",
                "automation-restored",
                "automation-published",
                "automation-unpublished",
                "automation-updated",
                "form-created",
                "form-updated",
                "form-deleted",
                "form-restored",
                "form-downloaded",
                "table-created",
                "table-deleted",
                "table-restored",
                "table-permanently-deleted",
                "table-field-added",
                "table-field-updated",
                "table-field-deleted",
                "table-view-added",
                "table-view-updated",
                "table-view-deleted",
                "table-page-added",
                "table-page-updated",
                "table-page-deleted",
                "table-widget-added",
                "table-widget-updated",
                "table-widget-deleted",
                "table-permission-changed",
                "table-data-downloaded",
                "table-data-imported",
                "table-data-batch-updated",
                "table-data-batch-deleted",
                "contact-data-downloaded",
                "contact-batch-updated",
                "chat-data-downloaded",
                "voice-agent-created",
                "voice-agent-updated",
                "voice-agent-deleted",
                "voice-agent-phone-number-inbound-agent-changed",
                "voice-agent-phone-number-outbound-agent-changed",
                "voice-agent-batch-call-created",
                "voice-agent-knowledge-base-created",
                "voice-agent-knowledge-base-updated",
                "drive-folder-created",
                "drive-folder-deleted",
                "drive-folder-renamed",
                "drive-file-added",
                "drive-file-deleted",
                "task-module-rules-rule-added",
                "task-module-rules-rule-updated",
                "task-module-rules-rule-deleted",
                "task-module-rules-rule-enabled",
                "task-module-rules-rule-disabled",
                "task-module-rules-enabled",
                "task-module-rules-disabled",
                "contact-module-rules-rule-added",
                "contact-module-rules-rule-updated",
                "contact-module-rules-rule-deleted",
                "contact-module-rules-rule-enabled",
                "contact-module-rules-rule-disabled",
                "contact-module-rules-enabled",
                "contact-module-rules-disabled",
                "module-created",
                "module-updated",
                "module-deleted",
                "relation-created",
                "relation-updated",
                "relation-deleted",
                "web-app-created",
                "web-app-updated",
                "web-app-deleted",
                "web-tab-created",
                "web-tab-updated",
                "web-tab-deleted",
                "developer-token-created",
                "developer-token-changed"
            ],
        };
    },
    methods: {
        ...mapActions({
            getUsers: "user/getUsers",
            getAdminUsers: "adminUser/getAdminUsers",
        }),
        getColumns() {
            console.log("getColumns", this.users);
            return [
                {
                    field: "",
                    key: "a",
                    type: "expand",
                    title: "",
                    width: 50,
                    align: "center",
                },
                {
                    field: "createdAt",
                    key: "createdAt",
                    title: this.$t("settings.dateAndTime"),
                    align: "left",
                    width: 180,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return (
                            <div class="date-cell">
                                <a-icon type="clock-circle" style="margin-right: 4px; color: #1890ff;" />
                                <span class="date-text">{moment(row.createdAt).format("DD.MM.YYYY")}</span>
                                <span class="time-text">{moment(row.createdAt).format("HH:mm:ss")}</span>
                            </div>
                        );
                    },
                },
                {
                    field: "actor",
                    key: "actor",
                    title: this.$t("settings.actor"),
                    align: "left",
                    width: 200,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        const actorName = this.getActorName(row);
                        const actorType = row.actor;
                        let iconType = 'user';
                        let color = '#1890ff';
                        if (actorType === 'system') {
                            iconType = 'robot';
                            color = '#722ed1';
                        } else if (actorType === 'chatbot') {
                            iconType = 'message';
                            color = '#52c41a';
                        } else if (actorType === 'visitor') {
                            iconType = 'team';
                            color = '#fa8c16';
                        }
                        return (
                            <div class="actor-cell">
                                <a-icon type={iconType} style={`color: ${color}; margin-right: 8px;`} />
                                <span class="actor-name">{actorName || '-'}</span>
                            </div>
                        );
                    },
                    filter: {
                        filterList: [
                            {
                                value: "system",
                                label: this.$t("auditLogs.actorSystem"),
                                selected: this.filter.actorIds.includes("system"),
                            },
                            {
                                value: "chatbot",
                                label: this.$t("auditLogs.actorChatbot"),
                                selected: this.filter.actorIds.includes("chatbot"),
                            },
                            {
                                value: "api",
                                label: this.$t("auditLogs.actorApi"),
                                selected: this.filter.actorIds.includes("api"),
                            },
                            {
                                value: "visitor",
                                label: this.$t("auditLogs.actorVisitor"),
                                selected: this.filter.actorIds.includes("visitor"),
                            },
                            {
                                value: "agent",
                                label: this.$t("auditLogs.actorAgent"),
                                selected: this.filter.actorIds.includes("agent"),
                            },
                            ...this.users.map((user) => {
                                return {
                                    value: user._id,
                                    label: user.name,
                                    selected: this.filter.actorIds.includes(user._id),
                                };
                            }),
                            ...(this.isAdmin ? this.adminUsers : []).map((user) => {
                                return {
                                    value: user._id,
                                    label: user.name + " " + this.$t("auditLogs.adminUserSuffix"),
                                    selected: this.filter.actorIds.includes(user._id),
                                };
                            }),
                        ],
                        isMultiple: true,
                        // filter confirm hook
                        filterConfirm: (filterList) => {
                            this.filter.actorIds = filterList.filter((x) => x.selected).map((x) => x.value);
                        },
                        // filter reset hook
                        filterReset: (filterList) => {
                            this.filter.actorIds = [];
                        },
                        // max height
                        maxHeight: 400,
                        width: 300,
                    },
                },
                {
                    field: "ipAddress",
                    key: "ipAddress",
                    title: this.$t("auditLogs.ipAddress"),
                    align: "left",
                    width: 150,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return (
                            <div class="ip-cell">
                                <a-icon type="global" style="margin-right: 4px; color: #52c41a;" />
                                <span class="ip-text">{row.ipAddress || "-"}</span>
                            </div>
                        );
                    },
                },
                {
                    field: "type",
                    key: "type",
                    title: this.$t("settings.event"),
                    align: "left",
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        const label = this.activityTypeLabels[row.type] || row.type;
                        return (
                            <div class="activity-type-cell">
                                <a-tag color="blue" style="margin: 0;">
                                    {label}
                                </a-tag>
                            </div>
                        );
                    },
                    // Ref: https://happy-coding-clans.github.io/vue-easytable/#/en/doc/table/header-filter-custom
                    filterCustom: {
                        defaultVisible: false,
                        render: ({ showFn, closeFn }, h) => {
                            const query = (this.activityTypeSearchQuery || "").toLowerCase().trim();
                            const options = this.activityTypes.filter((x) => {
                                const label = (x.label || "").toLowerCase();
                                const value = (x.value || "").toLowerCase();
                                return !query.length || label.includes(query) || value.includes(query);
                            });

                            const isSelected = (val) => {
                                return (this.activityTypeCustomSelected || []).includes(val);
                            };

                            const toggleSelected = (val) => {
                                const idx = (this.activityTypeCustomSelected || []).indexOf(val);
                                if (idx === -1) {
                                    this.activityTypeCustomSelected = (this.activityTypeCustomSelected || []).concat([val]);
                                } else {
                                    this.activityTypeCustomSelected = (this.activityTypeCustomSelected || []).filter((x) => x !== val);
                                }
                            };

                            const onCancel = () => {
                                this.activityTypeSearchQuery = "";
                                this.activityTypeCustomSelected = (this.filter.activityTypes || []).slice(0);
                                closeFn && closeFn();
                            };

                            const onReset = () => {
                                this.activityTypeSearchQuery = "";
                                this.activityTypeCustomSelected = [];
                                this.filter.activityTypes = [];
                                closeFn && closeFn();
                            };

                            const onConfirm = () => {
                                this.filter.activityTypes = (this.activityTypeCustomSelected || []).slice(0);
                                closeFn && closeFn();
                            };

                            return h("div", { style: { minWidth: "300px" } }, [
                                h("div", { style: { padding: "8px", borderBottom: "1px solid #e8e8e8" } }, [
                                    h("input", {
                                        domProps: { value: this.activityTypeSearchQuery },
                                        on: {
                                            input: (e) => {
                                                this.activityTypeSearchQuery = e.target.value;
                                            },
                                        },
                                        attrs: { placeholder: this.$t("message.search") || "Ara..." },
                                        style: {
                                            width: "100%",
                                            padding: "4px 8px",
                                            border: "1px solid #d9d9d9",
                                            borderRadius: "4px",
                                        },
                                    }),
                                ]),
                                h(
                                    "div",
                                    { style: { maxHeight: "300px", overflowY: "auto" } },
                                    options.map((opt) => {
                                        const checked = isSelected(opt.value);
                                        return h(
                                            "div",
                                            {
                                                key: opt.value,
                                                on: { click: () => toggleSelected(opt.value) },
                                                style: {
                                                    padding: "8px 12px",
                                                    cursor: "pointer",
                                                    background: checked ? "#e6f7ff" : "transparent",
                                                },
                                            },
                                            [
                                                h("input", {
                                                    attrs: { type: "checkbox", checked: checked },
                                                    style: { marginRight: "8px", pointerEvents: "none" },
                                                }),
                                                h("span", opt.label),
                                            ]
                                        );
                                    })
                                ),
                                h("div", { style: { padding: "8px", borderTop: "1px solid #e8e8e8", display: "flex", justifyContent: "space-between" } }, [
                                    h("span", { style: { cursor: "pointer", color: "#999" }, on: { click: () => onCancel() } }, this.$t("message.cancel")),
                                    h("div", [
                                        h(
                                            "span",
                                            { style: { cursor: "pointer", marginRight: "12px" }, on: { click: () => onReset() } },
                                            this.$t("chatWindow.all") || "Tümü"
                                        ),
                                        h(
                                            "span",
                                            {
                                                style: { cursor: "pointer", color: "#1890ff", fontWeight: 600 },
                                                on: { click: () => onConfirm() },
                                            },
                                            this.$t("chatWindow.filterTable") || "Filtrele"
                                        ),
                                    ]),
                                ]),
                            ]);
                        },
                    },
                },
            ];
        },
        loadNextPage() {
            this.cursor.page++;
            this.fetchActivities(true);
        },
        pageNumberChange(pageIndex) {
            this.cursor.page = pageIndex;
        },
        // page size change
        pageSizeChange(pageSize) {
            this.cursor.page = 1;
            this.cursor.perPage = pageSize;
        },
        updateFilters(filter) {
            this.filter = filter;
        },
        fetchActivities(concatMode = false) {
            this.loading = true;
            if (this.loadingInstance) {
            this.loadingInstance.show();
            }
            ACTIVITY_API.ACTIVITY.list(this.cursor, this.filter)
                .then((response) => {
                    const { items } = response.data;
                    if (concatMode) {
                        this.activities = this.activities.concat(items);
                    } else {
                        this.activities = items;
                    }
                    // Eğer gelen veri sayfası dolmuyorsa daha fazla veri yok
                    this.hasMore = items.length === this.cursor.perPage;
                })
                .catch((error) => {
                    console.error('Error fetching activities:', error);
                    this.$message.error('Aktiviteler yüklenirken bir hata oluştu');
                })
                .finally(() => {
                    this.loading = false;
                    if (this.loadingInstance) {
                    this.loadingInstance.close();
                    }
                });
        },

        getActorName(activity) {
            if (activity.actor === "user") {
                return this.$_.get(activity, "actorUser.name");
            } else if (activity.actor === "admin-user") {
                return this.$_.get(activity, "actorUser.name", "");
            } else if (activity.actor === "visitor") {
                return this.$_.get(activity, "refVisitor.fullname");
            } else if (activity.actor === "chatbot") {
                return "Chatbot";
            } else if (activity.actor === "system") {
                return "System (Supsis Ai)";
            }
        },
        getFilteredActivityTypes() {
            let filtered = this.activityTypes;
            if (this.activityTypeSearchQuery && this.activityTypeSearchQuery.trim()) {
                const query = this.activityTypeSearchQuery.toLowerCase().trim();
                filtered = this.activityTypes.filter((activity) => {
                    return activity.label.toLowerCase().includes(query) || 
                           activity.value.toLowerCase().includes(query);
                });
            }
            return filtered.map((activity) => {
                return {
                    value: activity.value,
                    label: activity.label,
                    selected: this.filter.activityTypes.includes(activity.value),
                };
            });
        },
        filterActivityTypes(input, option) {
            const label = option.componentOptions.children[0].text || '';
            const value = option.componentOptions.propsData.value || '';
            return label.toLowerCase().includes(input.toLowerCase()) || 
                   value.toLowerCase().includes(input.toLowerCase());
        },
        onFilterChange() {
            // Filter değiştiğinde otomatik uygula
            this.applyFilters();
        },
        /**
         * Tarih aralığı değiştiğinde çağrılır
         * @param {Array} dates - Moment nesneleri dizisi [startDate, endDate]
         */
        onDateRangeChange(dates) {
            this.dateRange = dates;
            if (dates && dates.length === 2) {
                // Başlangıç tarihini günün başına (00:00:00) ayarla
                this.filter.startDate = dates[0].startOf('day').toISOString();
                // Bitiş tarihini günün sonuna (23:59:59) ayarla
                this.filter.endDate = dates[1].endOf('day').toISOString();
                // İki tarih de seçildiğinde otomatik filtrele
                this.onFilterChange();
            } else {
                // Tarih seçimi temizlendiğinde
                this.filter.startDate = null;
                this.filter.endDate = null;
                // Temizlendiğinde de otomatik filtrele
                this.onFilterChange();
            }
        },
        applyFilters() {
            this.cursor.page = 1;
            this.fetchActivities();
        },
        resetFilters() {
            this.filter = {
                actorIds: [],
                startDate: null,
                endDate: null,
                activityTypes: [],
            };
            this.dateRange = [];
            this.cursor.page = 1;
            this.fetchActivities();
        },
        removeActorFilter(actorId) {
            this.filter.actorIds = this.filter.actorIds.filter(id => id !== actorId);
            this.applyFilters();
        },
        removeActivityTypeFilter(activityType) {
            this.filter.activityTypes = this.filter.activityTypes.filter(type => type !== activityType);
            this.applyFilters();
        },
        /**
         * Tarih filtresini kaldırır
         */
        removeDateFilter() {
            this.filter.startDate = null;
            this.filter.endDate = null;
            this.dateRange = [];
            this.applyFilters();
        },
        /**
         * Tarih aralığını formatlar (aktif filtreler için)
         * @returns {string} - Formatlanmış tarih aralığı
         */
        formatDateRange() {
            if (!this.filter.startDate || !this.filter.endDate) return '';
            const startDate = moment(this.filter.startDate).format('DD.MM.YYYY');
            const endDate = moment(this.filter.endDate).format('DD.MM.YYYY');
            return `${startDate} - ${endDate}`;
        },
        getActorLabel(actorId) {
            if (actorId === "system") return this.$t("auditLogs.actorSystem");
            if (actorId === "chatbot") return this.$t("auditLogs.actorChatbot");
            if (actorId === "api") return this.$t("auditLogs.actorApi");
            if (actorId === "visitor") return this.$t("auditLogs.actorVisitor");
            if (actorId === "agent") return this.$t("auditLogs.actorAgent");
            const user = this.users.find((u) => u._id === actorId);
            if (user) return user.name;
            const adminUser = this.adminUsers.find((u) => u._id === actorId);
            if (adminUser) return adminUser.name + " " + this.$t("auditLogs.adminUserSuffix");
            return actorId;
        },
    },
    computed: {
        ...mapState({
            users: (state) => state.user.users.data,
            adminUsers: (state) => state.adminUser.adminUsers.data,
            isAdmin: (state) => state.auth.adminUser != null,
        }),
        activityTypes() {
            this.$i18n.locale;
            return this.activityTypeValues.map((value) => ({
                value: value,
                label: this.$t("auditLogs.activityType." + value) || value,
            }));
        },
        activityTypeLabels() {
            this.$i18n.locale;
            const labels = {};
            this.activityTypes.forEach((type) => {
                labels[type.value] = type.label;
            });
            return labels;
        },
        tableConfig() {
            this.$i18n.locale;
            const t = (key) => this.$t("auditLogs.tablePagination." + key) || key;
            return {
                pagination: {
                    goto: t("goto"),
                    page: "",
                    itemsPerPage: t("pagePer"),
                    total: (total) => (this.$t("auditLogs.tablePagination.total") || "Toplam") + " " + total,
                    prev5: t("prev5"),
                    next5: t("next5"),
                },
                table: {
                    confirmFilter: this.$t("chatWindow.filterTable"),
                    resetFilter: this.$t("chatWindow.all"),
                    cut: t("cut"),
                    copy: t("copy"),
                    insertRowAbove: t("insertRowAbove"),
                    insertRowBelow: t("insertRowBelow"),
                    removeRow: "$1 " + (t("removeRow") || "satırı kaldır"),
                    emptyRow: "$1 " + (t("emptyRow") || "satırı boşalt"),
                    emptyColumn: "$1 " + (t("emptyColumn") || "sütunu boşalt"),
                    emptyCell: t("emptyCell"),
                    leftFixedColumnTo: t("leftFixedColumnTo"),
                    cancelLeftFixedColumnTo: t("cancelLeftFixedColumnTo"),
                    rightFixedColumnTo: t("rightFixedColumnTo"),
                    cancelRightFixedColumnTo: t("cancelRightFixedColumnTo"),
                },
            };
        },
        hasActiveFilters() {
            return (this.filter.actorIds && this.filter.actorIds.length > 0) ||
                   (this.filter.activityTypes && this.filter.activityTypes.length > 0) ||
                   (this.filter.startDate && this.filter.endDate);
        }
    },
    mounted() {
        VeLocale.use(this.tableConfig);
        this.loadingInstance = this.$veLoading({
            target: document.querySelector("#audit-logs-table"),
            name: "wave",
        });
        this.fetchActivities();
        if (this.isAdmin) {
            this.getAdminUsers({
                page: 1,
                perPage: 50,
            });
        }
    },
    watch: {
        filter: {
            deep: true,
            handler() {
                this.cursor.page = 1;
                this.fetchActivities();
            },
        },
        loading: {
            handler() {},
        },
        "$i18n.locale"() {
            VeLocale.use(this.tableConfig);
        },
    },
});
</script>

<style lang="scss">
#audit-logs {
    .page-header {
        margin-bottom: 24px;
    }

    .audit-logs-filter-panel {
        margin-bottom: 24px !important;

        .audit-logs-filter-card {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
            border-radius: 8px !important;

            .audit-logs-filter-content {
                .audit-logs-filter-row {
                    display: grid !important;
                    grid-template-columns: 1fr 1fr 1fr !important;
                    gap: 16px !important;
                    margin-bottom: 16px !important;

                    @media (max-width: 1200px) {
                        grid-template-columns: 1fr 1fr !important;
                    }

                    @media (max-width: 768px) {
                        grid-template-columns: 1fr !important;
                    }

                    .audit-logs-filter-item {
                        .audit-logs-filter-label {
                            display: flex !important;
                            align-items: center !important;
                            gap: 6px !important;
                            font-weight: 500 !important;
                            margin-bottom: 8px !important;
                            color: #262626 !important;
                            font-size: 14px !important;

                            .anticon {
                                color: #1890ff !important;
                            }
                        }

                        .audit-logs-actor-select,
                        .audit-logs-activity-type-select {
                            width: 100% !important;
                        }
                    }
                }

                .audit-logs-filter-actions {
                    display: flex !important;
                    justify-content: flex-start !important;
                    gap: 8px !important;
                    padding-top: 16px !important;
                    border-top: 1px solid #f0f0f0 !important;
                }
            }
        }

        .audit-logs-active-filters {
            margin-top: 16px !important;
            padding: 12px 16px !important;
            background: #f5f5f5 !important;
            border-radius: 6px !important;
            display: flex !important;
            align-items: center !important;
            gap: 12px !important;
            flex-wrap: nowrap !important;
            overflow-x: auto !important;
            overflow-y: hidden !important;

            .audit-logs-active-filters-label {
                display: flex !important;
                align-items: center !important;
                gap: 6px !important;
                font-weight: 500 !important;
                color: #595959 !important;
                font-size: 14px !important;
                white-space: nowrap !important;
                flex-shrink: 0 !important;
            }

            .audit-logs-filter-tags {
                display: flex !important;
                flex-wrap: nowrap !important;
                gap: 8px !important;
                align-items: center !important;
                min-width: 0 !important;

                .audit-logs-filter-tag {
                    flex-shrink: 0 !important;
                }
            }
        }
    }

    .table-section {
        .table-card {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
            border-radius: 8px;

            .table-header {
                margin-bottom: 16px;
                padding-bottom: 16px;
                border-bottom: 1px solid #f0f0f0;

                .table-title {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 16px;
                    font-weight: 600;
                    color: #262626;

                    .anticon {
                        color: #1890ff;
                    }
                }
            }

            .empty-state {
                padding: 60px 20px;
            }

            .audit-table {
                .date-cell {
                    display: flex;
                    align-items: center;
                    flex-direction: column;
                    align-items: flex-start;

                    .date-text {
                        font-weight: 500;
                        color: #262626;
                        font-size: 13px;
                    }

                    .time-text {
                        font-size: 12px;
                        color: #8c8c8c;
                        margin-top: 2px;
                    }
                }

                .actor-cell {
                    display: flex;
                    align-items: center;

                    .actor-name {
                        font-weight: 500;
                        color: #262626;
                    }
                }

                .ip-cell {
                    display: flex;
                    align-items: center;

                    .ip-text {
                        font-family: 'Courier New', monospace;
                        font-size: 12px;
                        color: #1890ff;
                        font-weight: 500;
                    }
                }

                .activity-type-cell {
                    .ant-tag {
                        margin: 0;
                        padding: 2px 8px;
                        border-radius: 4px;
                    }
                }
            }
        }
    }

    .actions {
        display: flex;
        justify-content: center;
        margin-top: 24px;
        padding: 20px 0;
    }

    // Table row hover effect
    :deep(.ve-table-body-tr:hover) {
        background-color: #fafafa !important;
        transition: background-color 0.2s;
    }

    // Expand row styling
    :deep(.ve-table-expand-row) {
        background-color: #fafafa;
        padding: 16px;

        .my-editor {
            border-radius: 4px;
            border: 1px solid #e8e8e8;
        }
    }
}
</style>
